import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import "./DashboardEmbedScope.css";

import FiltersPanel from "./components/FiltersPanel";
import MetricsSummary from "./components/MetricsSummary";
import EmployeeList from "./components/EmployeeList";
import EmployeeDetailPanel from "./components/EmployeeDetailPanel";
import usePaginatedData from "./hooks/usePaginatedData";
import DashboardPanelSwitcher from "../Common/DashboardPanelSwitcher";

import {
  getFiscalYears,
  getMonths,
  getDivisions,
  getStates,
  getTerritories,
  getSummary,
  getEmployees,
  getEmployeeMain,
  getEmployeeBrandPerformance,
  getEmployeeMissedDoctors,
  getEmployeePatchCoverage,
  getEmployeeSpecialtyCoverage,
  getEmployeeMonthlyCoverageTrend,
  getEmployeeWeeklyPerformance,
} from "../../services/salesDashboardApi";

const EMPTY_FILTERS = {
  month: "",
  division: null,
  state: null,
  territory: null,
};

export default function SalesDashboard() {
  const [fiscalYear, setFiscalYear] = useState("");
  const [fiscalYearOptions, setFiscalYearOptions] = useState([]);
  const [months, setMonths] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [divisionOptions, setDivisionOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);

  const [dropdownLoading, setDropdownLoading] = useState({
    months: false,
    divisions: false,
    states: false,
    territories: false,
  });
  const [dropdownErrors, setDropdownErrors] = useState({
    months: "",
    divisions: "",
    states: "",
    territories: "",
  });

  const [filterError, setFilterError] = useState("");
  const [hasApplied, setHasApplied] = useState(false);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [summaryStatus, setSummaryStatus] = useState({ loading: false, error: "" });
  const [employeesStatus, setEmployeesStatus] = useState({ loading: false, error: "" });

  const summaryAbortRef = useRef(null);
  const employeesAbortRef = useRef(null);
  const activeRequestIdRef = useRef(0);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const tablePageLimit = limit;

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const employeeDetailAbortRef = useRef(null);
  const activeEmployeeRequestIdRef = useRef(0);

  const EMPTY_DETAIL_SECTIONS = useMemo(
    () => ({
      main: { loading: false, error: "", loaded: false },
      brandPerformance: { loading: false, error: "", loaded: false },
      missedDoctors: { loading: false, error: "", loaded: false },
      patchCoverage: { loading: false, error: "", loaded: false },
      specialtyCoverage: { loading: false, error: "", loaded: false },
      monthlyCoverageTrend: { loading: false, error: "", loaded: false },
      weeklyPerformance: { loading: false, error: "", loaded: false },
    }),
    []
  );

  const [detailSections, setDetailSections] = useState(EMPTY_DETAIL_SECTIONS);
  const [unit, setUnit] = useState("lakh");

  useEffect(() => {
    document.body.classList.add("emcure-dashboard-route-scroll");
    return () => document.body.classList.remove("emcure-dashboard-route-scroll");
  }, []);

  const canApply = useMemo(
    () => Boolean(filters.month && filters.division && filters.state && filters.territory),
    [filters]
  );

  const buildDashboardParams = useCallback(
    () => ({
      month: filters.month,
      divisionId: filters.division?.name || filters.division?.label || "",
      stateId: filters.state?.value || filters.state?.label || "",
      territoryId: filters.territory?.value || filters.territory?.label || "",
    }),
    [filters]
  );

  const detailParams = useMemo(
    () => ({
      month: filters.month,
      division: filters.division?.name || filters.division?.label || filters.division?.value || "",
      state: filters.state?.name || filters.state?.label || filters.state?.value || "",
      territory: filters.territory?.name || filters.territory?.label || filters.territory?.value || "",
    }),
    [filters]
  );

  const detailResetKey = useMemo(
    () =>
      JSON.stringify({
        employeeId: selectedEmployeeId,
        ...detailParams,
      }),
    [selectedEmployeeId, detailParams]
  );

  const fetchBrandPerformancePage = useCallback(
    ({ page: p, limit: lim, signal }) =>
      getEmployeeBrandPerformance(selectedEmployeeId, detailParams, { signal, page: p, limit: lim }),
    [selectedEmployeeId, detailParams]
  );

  const fetchMissedDoctorsPage = useCallback(
    ({ page: p, limit: lim, signal }) =>
      getEmployeeMissedDoctors(selectedEmployeeId, detailParams, { signal, page: p, limit: lim }),
    [selectedEmployeeId, detailParams]
  );

  const fetchPatchCoveragePage = useCallback(
    ({ page: p, limit: lim, signal }) =>
      getEmployeePatchCoverage(selectedEmployeeId, detailParams, { signal, page: p, limit: lim }),
    [selectedEmployeeId, detailParams]
  );

  const fetchSpecialtyCoveragePage = useCallback(
    ({ page: p, limit: lim, signal }) =>
      getEmployeeSpecialtyCoverage(selectedEmployeeId, detailParams, { signal, page: p, limit: lim }),
    [selectedEmployeeId, detailParams]
  );

  const brandPerformanceQuery = usePaginatedData({
    enabled: Boolean(selectedEmployeeId),
    fetchPage: fetchBrandPerformancePage,
    limit: tablePageLimit,
    initialPage: 1,
    resetKey: detailResetKey,
  });

  const missedDoctorsQuery = usePaginatedData({
    enabled: Boolean(selectedEmployeeId),
    fetchPage: fetchMissedDoctorsPage,
    limit: tablePageLimit,
    initialPage: 1,
    resetKey: detailResetKey,
  });

  const patchCoverageQuery = usePaginatedData({
    enabled: Boolean(selectedEmployeeId),
    fetchPage: fetchPatchCoveragePage,
    limit: tablePageLimit,
    initialPage: 1,
    resetKey: detailResetKey,
  });

  const specialtyCoverageQuery = usePaginatedData({
    enabled: Boolean(selectedEmployeeId),
    fetchPage: fetchSpecialtyCoveragePage,
    limit: tablePageLimit,
    initialPage: 1,
    resetKey: detailResetKey,
  });

  const fetchEmployeesForCurrentFilters = useCallback(
    async (requestId = activeRequestIdRef.current) => {
      const params = buildDashboardParams();

      employeesAbortRef.current?.abort?.();
      const controller = new AbortController();
      employeesAbortRef.current = controller;

      setEmployeesStatus({ loading: true, error: "" });

      try {
        const employeeResponse = await getEmployees(
          params.month,
          params.divisionId,
          params.stateId,
          params.territoryId,
          page,
          limit,
          { signal: controller.signal }
        );

        if (activeRequestIdRef.current !== requestId) return;

        setEmployees(employeeResponse.data || []);
        setTotalPages(employeeResponse.total_pages || 1);
        setEmployeesStatus({ loading: false, error: "" });
      } catch (err) {
        if (controller.signal?.aborted) return;
        if (activeRequestIdRef.current !== requestId) return;

        setEmployees([]);
        setTotalPages(1);
        setEmployeesStatus({ loading: false, error: "Failed to load employees. Please try again." });
      }
    },
    [buildDashboardParams, page, limit]
  );

  useEffect(() => {
    fetchMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasApplied) fetchEmployeesForCurrentFilters();
  }, [page, hasApplied, fetchEmployeesForCurrentFilters]);

  useEffect(() => {
    if (filters.month) fetchDivisions(filters.month, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month]);

  useEffect(() => {
    return () => {
      summaryAbortRef.current?.abort?.();
      employeesAbortRef.current?.abort?.();
      employeeDetailAbortRef.current?.abort?.();
    };
  }, []);

  const fetchFiscalYears = async () => {
    const data = await getFiscalYears();
    setFiscalYearOptions(data);
  };

  const handleFiscalYearChange = async (fy) => {
    setFiscalYear(fy);
    const data = await getMonths(fy?.id);
    setMonths(data);
    setFilters({ month: "", division: null, state: null, territory: null });
  };

  async function fetchMonths() {
    setDropdownLoading((prev) => ({ ...prev, months: true }));
    setDropdownErrors((prev) => ({ ...prev, months: "" }));
    try {
      const data = await getMonths();
      setMonths(data);
      setFilters((prev) => ({ ...prev, month: prev.month || data?.[0]?.id || "" }));
    } catch (err) {
      setDropdownErrors((prev) => ({ ...prev, months: "Failed to load months." }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, months: false }));
    }
  }

  const fetchDivisions = useCallback(
    async (monthId, force = false) => {
      if (!monthId) return;
      if (divisionOptions.length && !force) return;

      setDropdownLoading((prev) => ({ ...prev, divisions: true }));
      setDropdownErrors((prev) => ({ ...prev, divisions: "" }));
      try {
        const data = await getDivisions(monthId);
        setDivisionOptions(data);
      } catch (err) {
        setDropdownErrors((prev) => ({ ...prev, divisions: "Failed to load divisions." }));
      } finally {
        setDropdownLoading((prev) => ({ ...prev, divisions: false }));
      }
    },
    [divisionOptions.length]
  );

  async function fetchStates(force = false) {
    if (!filters.month || !filters.division?.id) return;
    if (stateOptions.length && !force) return;

    setDropdownLoading((prev) => ({ ...prev, states: true }));
    setDropdownErrors((prev) => ({ ...prev, states: "" }));
    try {
      const data = await getStates(filters.month, filters.division.id);
      setStateOptions(data);
    } catch (err) {
      setDropdownErrors((prev) => ({ ...prev, states: "Failed to load states." }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, states: false }));
    }
  }

  async function fetchTerritories(force = false) {
    if (!filters.month || !filters.division?.id || !filters.state?.id) return;
    if (territoryOptions.length && !force) return;

    setDropdownLoading((prev) => ({ ...prev, territories: true }));
    setDropdownErrors((prev) => ({ ...prev, territories: "" }));
    try {
      const data = await getTerritories(filters.month, filters.division.id, filters.state.id);
      setTerritoryOptions(data);
    } catch (err) {
      setDropdownErrors((prev) => ({ ...prev, territories: "Failed to load territories." }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, territories: false }));
    }
  }

  function resetDashboardData() {
    setHasApplied(false);
    setSummary(null);
    setEmployees([]);
    setSelectedEmployeeId(null);
    setEmployeeDetail(null);
    setSummaryStatus({ loading: false, error: "" });
    setEmployeesStatus({ loading: false, error: "" });
    summaryAbortRef.current?.abort?.();
    employeesAbortRef.current?.abort?.();
    employeeDetailAbortRef.current?.abort?.();
    setDetailSections(EMPTY_DETAIL_SECTIONS);
  }

  const fetchSummaryForCurrentFilters = async (requestId) => {
    const params = buildDashboardParams();
    summaryAbortRef.current?.abort?.();
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setSummaryStatus({ loading: true, error: "" });

    try {
      const summaryData = await getSummary(params, { signal: controller.signal });
      if (activeRequestIdRef.current !== requestId) return;
      setSummary(summaryData);
      setSummaryStatus({ loading: false, error: "" });
    } catch (err) {
      if (controller.signal?.aborted) return;
      if (activeRequestIdRef.current !== requestId) return;
      setSummary(null);
      setSummaryStatus({ loading: false, error: "Failed to load summary metrics. Please try again." });
    }
  };

  function handleFilterChange(key, value) {
    setPage(1);
    setFilterError("");

    if (key === "month") {
      setFilters({ month: value, division: null, state: null, territory: null });
      setDivisionOptions([]);
      setStateOptions([]);
      setTerritoryOptions([]);
      resetDashboardData();
      return;
    }
    if (key === "division") {
      setFilters((prev) => ({ ...prev, division: value, state: null, territory: null }));
      setStateOptions([]);
      setTerritoryOptions([]);
      resetDashboardData();
      return;
    }
    if (key === "state") {
      setFilters((prev) => ({ ...prev, state: value, territory: null }));
      setTerritoryOptions([]);
      resetDashboardData();
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    resetDashboardData();
  }

  async function handleApply() {
    if (!canApply) {
      setFilterError("Please select Month, Division, State, and Territory.");
      return;
    }
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    setHasApplied(true);
    setSelectedEmployeeId(null);
    setEmployeeDetail(null);
    setDetailSections(EMPTY_DETAIL_SECTIONS);
    setSummary(null);
    setEmployees([]);
    setTotalPages(1);

    fetchSummaryForCurrentFilters(requestId);
    fetchEmployeesForCurrentFilters(requestId);
  }

  async function handleEmployeeSelect(employeeOrCode) {
    const employee =
      typeof employeeOrCode === "object" && employeeOrCode !== null
        ? employeeOrCode
        : employees.find((e) => e.employee_code === employeeOrCode || e.id === employeeOrCode) ?? {
            employee_code: String(employeeOrCode),
            id: String(employeeOrCode),
          };

    const empCode = employee.employee_code || employee.id || "";

    const requestId = activeEmployeeRequestIdRef.current + 1;
    activeEmployeeRequestIdRef.current = requestId;

    employeeDetailAbortRef.current?.abort?.();
    const controller = new AbortController();
    employeeDetailAbortRef.current = controller;

    setSelectedEmployeeId(empCode);
    setEmployeeDetail(null);
    setDetailSections({
      main: { loading: true, error: "", loaded: false },
      brandPerformance: { loading: false, error: "", loaded: false },
      missedDoctors: { loading: false, error: "", loaded: false },
      patchCoverage: { loading: false, error: "", loaded: false },
      specialtyCoverage: { loading: false, error: "", loaded: false },
      monthlyCoverageTrend: { loading: true, error: "", loaded: false },
      weeklyPerformance: { loading: true, error: "", loaded: false },
    });

    const commonParams = detailParams;

    const setSection = (key, patch) =>
      setDetailSections((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const guard = () =>
      !controller.signal?.aborted && activeEmployeeRequestIdRef.current === requestId;

    (async () => {
      try {
        const main = await getEmployeeMain(empCode, commonParams, { signal: controller.signal });
        if (!guard()) return;
        if (main) setEmployeeDetail((prev) => ({ ...(prev || {}), ...main }));
        setSection("main", { loading: false, error: "", loaded: true });
      } catch (err) {
        if (!guard()) return;
        setSection("main", { loading: false, error: "Failed to load employee summary.", loaded: true });
      }
    })();

    (async () => {
      try {
        const monthly = await getEmployeeMonthlyCoverageTrend(empCode, commonParams, {
          signal: controller.signal,
        });
        if (!guard()) return;
        if (monthly) setEmployeeDetail((prev) => ({ ...(prev || {}), monthlyCoverageTrend: monthly }));
        setSection("monthlyCoverageTrend", { loading: false, error: "", loaded: true });
      } catch (err) {
        if (!guard()) return;
        setSection("monthlyCoverageTrend", { loading: false, error: "Failed to load monthly trend.", loaded: true });
      }
    })();

    (async () => {
      try {
        const weekly = await getEmployeeWeeklyPerformance(empCode, commonParams, { signal: controller.signal });
        if (!guard()) return;
        if (weekly) setEmployeeDetail((prev) => ({ ...(prev || {}), weeklyPerformance: weekly }));
        setSection("weeklyPerformance", { loading: false, error: "", loaded: true });
      } catch (err) {
        if (!guard()) return;
        setSection("weeklyPerformance", { loading: false, error: "Failed to load weekly performance.", loaded: true });
      }
    })();
  }

  const panelData = {
    ...(employeeDetail || {}),
    brandPerformance: brandPerformanceQuery.data,
    missedDoctors: missedDoctorsQuery.data,
    patchCoverage: patchCoverageQuery.data,
    specialtyCoverage: specialtyCoverageQuery.data,
  };

  const panelSections = {
    ...detailSections,
    brandPerformance: {
      ...detailSections.brandPerformance,
      loading: brandPerformanceQuery.loading,
      error: brandPerformanceQuery.error,
      loaded: brandPerformanceQuery.loaded,
      page: brandPerformanceQuery.page,
      totalPages: brandPerformanceQuery.totalPages,
      limit: tablePageLimit,
    },
    missedDoctors: {
      ...detailSections.missedDoctors,
      loading: missedDoctorsQuery.loading,
      error: missedDoctorsQuery.error,
      loaded: missedDoctorsQuery.loaded,
      page: missedDoctorsQuery.page,
      totalPages: missedDoctorsQuery.totalPages,
      limit: tablePageLimit,
    },
    patchCoverage: {
      ...detailSections.patchCoverage,
      loading: patchCoverageQuery.loading,
      error: patchCoverageQuery.error,
      loaded: patchCoverageQuery.loaded,
      page: patchCoverageQuery.page,
      totalPages: patchCoverageQuery.totalPages,
      limit: tablePageLimit,
    },
    specialtyCoverage: {
      ...detailSections.specialtyCoverage,
      loading: specialtyCoverageQuery.loading,
      error: specialtyCoverageQuery.error,
      loaded: specialtyCoverageQuery.loaded,
      page: specialtyCoverageQuery.page,
      totalPages: specialtyCoverageQuery.totalPages,
      limit: tablePageLimit,
    },
  };

  const handleSectionPageChange = (key, nextPage) => {
    const clamp = (p, total) => {
      const n = Number(p);
      if (!Number.isFinite(n)) return 1;
      if (!Number.isFinite(total) || total <= 0) return Math.max(1, n);
      return Math.max(1, Math.min(n, total));
    };

    if (key === "brandPerformance")
      brandPerformanceQuery.setPage(clamp(nextPage, brandPerformanceQuery.totalPages));
    else if (key === "missedDoctors")
      missedDoctorsQuery.setPage(clamp(nextPage, missedDoctorsQuery.totalPages));
    else if (key === "patchCoverage")
      patchCoverageQuery.setPage(clamp(nextPage, patchCoverageQuery.totalPages));
    else if (key === "specialtyCoverage")
      specialtyCoverageQuery.setPage(clamp(nextPage, specialtyCoverageQuery.totalPages));
  };

  return (
    <div className="emcure-sales-dashboard-embed dashboard-page">
      <header className="dashboard-header-new">
        <div className="dashboard-header-top">
          <div className="brand-lockup-col">
            <span className="brand-name">Emcure</span>
            <span className="brand-product">AI Sales Supervisor</span>
          </div>
          <DashboardPanelSwitcher direction="down" />
        </div>
      </header>

      <main className="dashboard-shell">
        <FiltersPanel
          fiscalYear={fiscalYear}
          fiscalYearOptions={fiscalYearOptions}
          onFiscalYearOpen={fetchFiscalYears}
          onFiscalYearChange={handleFiscalYearChange}
          months={months}
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApply}
          canApply={canApply}
          filterError={filterError}
          dropdownLoading={dropdownLoading}
          dropdownErrors={dropdownErrors}
          divisionOptions={divisionOptions}
          stateOptions={stateOptions}
          territoryOptions={territoryOptions}
          onDivisionOpen={() => fetchDivisions(filters.month)}
          onStateOpen={() => fetchStates()}
          onTerritoryOpen={() => fetchTerritories()}
          dashboardLoading={summaryStatus.loading || employeesStatus.loading}
        />

        {hasApplied && (
          <>
            <MetricsSummary
              loading={summaryStatus.loading}
              error={summaryStatus.error}
              summary={summary}
              unit={unit}
              setUnit={setUnit}
            />

            <EmployeeList
              loading={employeesStatus.loading}
              error={employeesStatus.error}
              employees={employees}
              selectedEmployeeId={selectedEmployeeId}
              onSelect={handleEmployeeSelect}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}

        {selectedEmployeeId && (
          <EmployeeDetailPanel
            loading={false}
            error=""
            data={panelData}
            unit={unit}
            sections={panelSections}
            onSectionPageChange={handleSectionPageChange}
          />
        )}
      </main>
    </div>
  );
}